import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Modal,
  Button,
  Form,
  Card,
  CardBody,
  Spinner,
} from "react-bootstrap";
import { useNavigate, useNavigation } from "react-router-dom";

import { CircleLoader } from "react-spinners";
import BeatLoader from "react-spinners/BeatLoader";
import { useSnackbar } from "../../../utils/snackbar";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import FormInput from "../../../components/FormInput";
import CreatableSelect from "react-select/creatable";
import { SingleValue } from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from "react-icons/fa";
import { Dropdown, DropdownMenu, DropdownItem, CardHeader } from "reactstrap";

import moment from "moment";
import { useParams } from "react-router-dom";

import {
  getAllClients,
  deleteClient,
  deleteSelectedClients,
} from "../../../api";
import {
  createUnit,
  createVat,
  getAllUnits,
  getAllVats,
  createProduct,
  updateProduct,
  deleteProduct,
  deleteSelectedProducts,
  getAllProducts,
} from "../../../api";
import {
  createInvoice,
  getAllInvoices,
  getInvoice,
  updateInvoice,
} from "../../../api";

import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../../components/LanguageSelector";

interface OptionType {
  label: string;
  value: string;
}

interface ItemType {
  item_title: string | null;
  item_id: string | null;
}

interface FormDataType {
  selectedInvoiceTypeValue: string;
  productOptions: OptionType[];
  serviceOptions: OptionType[];
  isDiscountBasicAmount: boolean;
  isAddDiscount: boolean;
}

interface RowDataType {
  id: string;
  userId: number;
  name_invoice_recipient: string;
  email: string;
  account_holder?: string;
  apt_suite?: string;
  bank?: string;
  bank_type?: string;
  bic?: string;
  city?: string;
  company_name?: string;
  country_type?: string;
  create_at?: string;
  currency_type?: string;
  eori_number?: string;
  fax?: string;
  gender_type?: string;
  iban?: string;
  mobile_number?: string;
  phone_number?: string;
  postal_code?: string;
  state?: string;
  street?: string;
  vat_id?: string;
  website?: string;
  isChecked?: boolean; // Checkbox state for each row
}

interface ClientCustomDropdownProps {
  clientData: RowDataType[];
  handleSelectClient: (client_name: string | null, id: string | null) => void;
  defaultValue: string | null;
}

interface ProductTypeProps {
  id: number | string;
  allProducts: ProductType[];
  handleSelectProduct: (id: number | string, value: InvoiceItem) => void;
  updateItem: (
    id: string | number,
    field: keyof InvoiceItem,
    value: any,
  ) => void;
  defaultValue: string | null | undefined;
  defaultTitle: string | null | undefined;
}

interface InvoiceItem {
  id: number | string;
  item?: ItemType;
  service_product_number?: string;
  description?: string;
  qty?: string;
  invoice_type?: string;
  unit_type?: string | number | null;
  unit_price?: string;
  selected_date?: Date | null;
  vat_type?: OptionType | null;
  total_cost_net?: string | number;
  total_cost_gross?: string | number;
  is_add_discount?: boolean;
  discount_type?: string;
  discount_price?: string | number;
  discount_percentage?: string | number;
  discounted_total_price_net?: string | number;
  discounted_total_price_gross?: string | number;
}

interface ProductType {
  id: number | string;
  item?: any;
  service_product_number?: string;
  description?: string;
  qty?: string;
  invoice_type?: string;
  unit_type?: string | number | null;
  unit_price?: string;
  selected_date?: Date | null;
  vat_type?: OptionType | null;
  total_cost_net?: string | number;
  total_cost_gross?: string | number;
  is_add_discount?: boolean;
  discount_type?: string;
  discount_price?: string | number;
  discount_percentage?: string | number;
  discounted_total_price_net?: string | number;
  discounted_total_price_gross?: string | number;
}

interface InvoiceData {
  items?: InvoiceItem[];
  client: { client_name: string | null; id: string | null };
  invoice_date: Date | null;
  due_date: Date | null | undefined;
  invoice_number: string;
  po_number: string;
}

const CustomInput = React.forwardRef<HTMLInputElement, any>(
  ({ value, onClick }, ref) => (
    <div className="input-group">
      <input
        type="text"
        className="form-control custom-datepicker"
        value={value}
        onClick={onClick}
        ref={ref}
        placeholder=""
        readOnly
        style={{ cursor: "pointer" }}
      />
      <span
        className="input-group-text bg-white"
        style={{ cursor: "pointer" }}
        onClick={onClick}
      >
        <FaCalendarAlt />
      </span>
    </div>
  ),
);

const ClientCustomDropdown: React.FC<ClientCustomDropdownProps> = ({
  clientData,
  handleSelectClient,
  defaultValue,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [selected, setSelected] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  useEffect(() => {
    setInputValue(defaultValue ?? "");
  }, [defaultValue]);

  const handleFocus = () => {
    setDropdownOpen(true);
  };

  const toggleDropdown = () => setDropdownOpen(prevState => !prevState);

  const handleSelect = (client_name: string, id: string) => {
    handleSelectClient(client_name, id);
    setInputValue(client_name ?? "");
    setSelected(client_name ?? "");
    setDropdownOpen(false);
  };

  const handleGoToNewClientPage = () => {
    navigate("/new-client");
    setDropdownOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setDropdownOpen(false);
    }
  };

  const handleChangeInput = (e: any) => {
    setInputValue(e.target.value);
    setSelected(null); // Reset selected state when typing
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  let filteredClients: RowDataType[] = [];

  // Filter clientData based on input value
  if (Array.isArray(clientData)) {
    filteredClients = selected
      ? clientData // Show full list after selection
      : clientData.filter(client =>
          client.name_invoice_recipient
            .toLowerCase()
            .includes(inputValue.toLowerCase()),
        );
  }

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <div
        style={{
          alignItems: "center",
          border: "1px solid #ccc",
          borderRadius: "4px",
          paddingLeft: "5px",
          paddingRight: "5px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center w-100 d-block">
          <input
            type="text"
            value={inputValue}
            onChange={handleChangeInput}
            onFocus={handleFocus}
            placeholder="Select or type..."
            style={{
              border: "none",
              outline: "none",
              padding: "5px",
              width: "100%",
            }}
          />
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
              aria-hidden="true"
              data-testid="combobox-chevrondown-icon"
              style={{ color: "rgb(42, 48, 61)" }}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </span>
        </div>
        {dropdownOpen && (
          <Dropdown
            isOpen={dropdownOpen}
            toggle={() => setDropdownOpen(false)}
            style={{ position: "absolute", top: "105%" }}
          >
            <DropdownMenu>
              <DropdownItem onClick={handleGoToNewClientPage}>
                + New Client
              </DropdownItem>
              {filteredClients.length > 0 ? (
                filteredClients.map(client => (
                  <DropdownItem
                    key={client.id}
                    onClick={() =>
                      handleSelect(client.name_invoice_recipient, client.id)
                    }
                  >
                    {client.name_invoice_recipient}
                  </DropdownItem>
                ))
              ) : (
                <DropdownItem disabled>No Clients Available</DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        )}
      </div>
    </div>
  );
};

const ProductCustomDropdown: React.FC<ProductTypeProps> = ({
  id,
  allProducts,
  handleSelectProduct,
  updateItem,
  defaultValue,
  defaultTitle,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState<any>("");
  const [selected, setSelected] = useState<any>("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (defaultValue) {
      const foundItemTitle = allProducts.find(
        product => product.id == defaultValue,
      )?.item;
      setInputValue(foundItemTitle ?? "");
    } else {
      setInputValue(defaultTitle ?? "");
    }
  }, [defaultValue]);

  const handleFocus = () => {
    setDropdownOpen(true);
  };

  const toggleDropdown = () => setDropdownOpen(prevState => !prevState);

  const handleSelect = (product: InvoiceItem) => {
    updateItem(id, "item", { item_title: product?.item, item_id: product?.id });
    setInputValue(product?.item ?? "");
    setSelected(product?.item ?? "");
    handleSelectProduct(id, product);

    setDropdownOpen(false);
  };

  const handleGoToNewClientPage = () => {
    navigate("/new-service");
    setDropdownOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setDropdownOpen(false);
    }
  };

  const handleChangeInput = (e: any) => {
    setInputValue(e.target.value);
    updateItem(id, "item", { item_title: e.target.value, item_id: null });
    setSelected(null); // Reset selected state when typing
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter clientData based on input value
  const filteredProducts = selected
    ? allProducts || []
    : (allProducts || []).filter(product =>
        (product?.item ?? "").toLowerCase().includes(inputValue.toLowerCase()),
      );

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <div
        style={{
          alignItems: "center",
          border: "1px solid #ccc",
          borderRadius: "4px",
          paddingLeft: "5px",
          paddingRight: "5px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center w-100 d-block">
          <input
            type="text"
            value={inputValue}
            onChange={handleChangeInput}
            onFocus={handleFocus}
            placeholder={t("Select or type...")}
            style={{
              border: "none",
              outline: "none",
              padding: "5px",
              width: "100%",
            }}
          />
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
              aria-hidden="true"
              data-testid="combobox-chevrondown-icon"
              style={{ color: "rgb(42, 48, 61)" }}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </span>
        </div>
        {dropdownOpen && (
          <Dropdown
            isOpen={dropdownOpen}
            toggle={() => setDropdownOpen(false)}
            style={{ position: "absolute", top: "105%" }}
          >
            <DropdownMenu>
              <DropdownItem onClick={handleGoToNewClientPage}>
                + {t("New Product")}
              </DropdownItem>
              {filteredProducts.length > 0 ? (
                filteredProducts.map(product => (
                  <DropdownItem
                    key={product.id}
                    onClick={() => handleSelect(product ?? "")}
                  >
                    {product?.item}
                  </DropdownItem>
                ))
              ) : (
                <DropdownItem disabled>
                  {t("No Clients Available")}
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        )}
      </div>
    </div>
  );
};

const NewInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const showSnackbar = useSnackbar();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(false);
  const [unitOptions, setUnitOptions] = useState<OptionType[]>([]);
  const [vatOptions, setVatOptions] = useState<OptionType[]>([]);

  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [selectedVatOption, setSelectedVatOption] = useState<OptionType | null>(
    null,
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [forms, setForms] = useState<FormDataType[]>([]);
  const [selectedInvoiceTypeValue, setSelectedInvoiceTypeValue] =
    useState<string>("1");
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [selectedDiscountType, setDiscountType] = useState<string>("1");
  const [clientData, setClientData] = useState<RowDataType[]>([]);

  const [invoiceDate, setInvoiceDate] = useState<Date | null>(new Date());

  const [invoiceData, setInvoiceData] = useState<InvoiceData>({
    client: { client_name: "", id: "" },
    invoice_date: new Date(),
    due_date: null,
    invoice_number: "",
    po_number: "",
    items: [],
  });

  const [allProducts, setItemData] = useState<InvoiceItem[]>([]);
  const [itemDataList, setItemDataList] = useState<InvoiceItem[]>([
    {
      id: Date.now(),
      item: { item_title: null, item_id: null },
      service_product_number: "",
      description: "",
      qty: "",
      invoice_type: "1",
      unit_type: null,
      unit_price: "",
      selected_date: null,
      vat_type: { value: "", label: "" },
      total_cost_net: "",
      total_cost_gross: "",
      is_add_discount: false,
      discount_type: "1",
      discount_price: "0",
      discount_percentage: "0",
      discounted_total_price_net: "",
      discounted_total_price_gross: "",
    },
  ]);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id) return;
      const payload = {
        invoiceId: id,
      };

      setLoading(true);
      try {
        const invoiceRes = await getInvoice(payload);
        if (invoiceRes && invoiceRes.success == true) {
          setInvoiceData(prevInvoiceData => ({
            ...prevInvoiceData,
            client: invoiceRes?.data.client,
          }));
          setInvoiceData(invoiceRes?.data);
          setItemDataList(invoiceRes?.data?.items);
          setLoading(false);
        }
      } catch (error: any) {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, []);

  useEffect(() => {}, [invoiceData]);

  useEffect(() => {
    const fetchAllVatsAndUnits = async () => {
      try {
        const vatsResponse = await getAllVats();
        const unitsResponse = await getAllUnits();

        if (unitsResponse.data) {
          const unitOptions = unitsResponse.data.map((unitData: any) => {
            const unitOption = {
              value: unitData.id,
              label: unitData.unit_name,
            };
            return unitOption;
          });

          setUnitOptions(unitOptions);
        }

        if (vatsResponse.data) {
          const vatOptions = vatsResponse.data.map((vatData: any) => {
            const vatOption = { value: vatData.id, label: vatData.vat_name };
            return vatOption;
          });

          setVatOptions(vatOptions);
        }
      } catch (error: any) {}
    };

    fetchAllVatsAndUnits();
  }, []);

  useEffect(() => {
    const fetchAllClients = async () => {
      try {
        const allClientsResponse: RowDataType[] = await getAllClients();
        setClientData(allClientsResponse);
      } catch (error: any) {}
    };

    fetchAllClients();
  }, []);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const response = await getAllProducts();
        setItemData(response.data);
      } catch (error: any) {}
    };

    fetchAllProducts();
  }, []);

  const handleSelectClient = (
    client_name: string | null,
    id: string | null,
  ) => {
    setInvoiceData(prevInvoiceData => ({
      ...prevInvoiceData,
      client: { client_name, id },
    }));
  };
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedValue(event.target.value);
  };

  const handleSaveInvoiceData = async () => {
    let payload;
    payload = {
      ...invoiceData,
      items: itemDataList,
    };

    setLoading(true);
    try {
      let response;
      if (!id) {
        response = await createInvoice(payload);
        if (response.success == true) {
          showSnackbar(response.message, "info");
        } else {
          showSnackbar(response.message, "error");
        }
        setLoading(false);
      } else {
        payload = {
          ...payload,
          invoiceId: id,
        };
        response = await updateInvoice(payload);
        if (response.success == true) {
          showSnackbar(response.message, "info");
        } else {
          showSnackbar(response.message, "error");
        }

        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const addItem = () => {
    setItemDataList([
      ...itemDataList,
      {
        id: Date.now(),
        item: { item_title: null, item_id: null },
        service_product_number: "",
        description: "",
        qty: "",
        invoice_type: "1",
        unit_type: null,
        unit_price: "",
        selected_date: null,
        vat_type: null,
        total_cost_net: "",
        total_cost_gross: "",
        is_add_discount: false,
        discount_type: "1",
        discount_price: "0",
        discount_percentage: "0",
        discounted_total_price_net: "",
        discounted_total_price_gross: "",
      },
    ]);
  };

  const updateItem = (
    id: string | number,
    field: keyof InvoiceItem,
    value: any,
  ) => {
    setItemDataList(
      itemDataList.map(item =>
        item.id === id
          ? {
              ...item,
              [field]: value,
              total_cost_net:
                field === "qty" || field === "unit_price"
                  ? (Number(field === "qty" ? value : item.qty) || 0) *
                    (Number(field === "unit_price" ? value : item.unit_price) ||
                      0)
                  : item.total_cost_net,
              discounted_total_price_net:
                field === "discount_type" ||
                field === "discount_price" ||
                field === "discount_percentage" ||
                field === "vat_type" ||
                field === "is_add_discount"
                  ? (field == "discount_type" ? value : item.discount_type) ==
                    "1"
                    ? Number(item.total_cost_net) -
                      Number(
                        field == "discount_price" ? value : item.discount_price,
                      )
                    : (Number(item.total_cost_net) *
                        (100 -
                          Number(
                            field == "discount_percentage"
                              ? value
                              : item.discount_percentage,
                          ))) /
                      100
                  : item.discounted_total_price_net,
              total_cost_gross:
                field === "qty" ||
                field === "unit_price" ||
                field === "vat_type"
                  ? (Number(field === "qty" ? value : item.qty) || 0) *
                    (Number(field === "unit_price" ? value : item.unit_price) ||
                      0) *
                    (1 +
                      Number(
                        field === "vat_type" &&
                          typeof value === "object" &&
                          value !== null &&
                          "label" in value
                          ? value?.label
                          : item.vat_type?.label,
                      ) /
                        100)
                  : item.total_cost_gross,
              discounted_total_price_gross:
                field === "discount_type" ||
                field === "discount_price" ||
                field === "discount_percentage" ||
                field == "vat_type" ||
                field === "is_add_discount"
                  ? (field == "discount_type" ? value : item.discount_type) ==
                    "1"
                    ? (Number(item.total_cost_net) -
                        Number(
                          field == "discount_price"
                            ? value
                            : item.discount_price,
                        )) *
                      (1 +
                        Number(
                          field === "vat_type" &&
                            typeof value === "object" &&
                            value !== null &&
                            "label" in value
                            ? value?.label
                            : item.vat_type?.label,
                        ) /
                          100)
                    : ((Number(item.total_cost_net) *
                        (100 -
                          Number(
                            field == "discount_percentage"
                              ? value
                              : item.discount_percentage,
                          ))) /
                        100) *
                      (1 +
                        Number(
                          field === "vat_type" &&
                            typeof value === "object" &&
                            value !== null &&
                            "label" in value
                            ? value?.label
                            : item.vat_type?.label,
                        ) /
                          100)
                  : item.discounted_total_price_net,
            }
          : item,
      ),
    );
  };

  const deleteItem = (id: string | number) => {
    setItemDataList(itemDataList.filter(itemData => itemData.id !== id));
  };

  const handleAddDiscount = (index: string | number) => {
    const updatedItemData = itemDataList.map((itemData, i) =>
      itemData.id === index
        ? { ...itemData, is_add_discount: !itemData.is_add_discount }
        : itemData,
    );
    setItemDataList(updatedItemData);
  };

  // Remove a specific form
  const handleRemoveForm = (index: number) => {
    const updatedForms = forms.filter((_, i) => i !== index);
    setForms(updatedForms);
  };

  const handleChange = (newValue: OptionType | null) => {
    setSelectedOption(newValue);
  };

  const handleInvoiceTypeChange = (selectedInvoiceTypeValue: string) => {
    setSelectedOption(null);
    setSelectedInvoiceTypeValue(selectedInvoiceTypeValue);
  };

  const handleCreateUnit = async (inputValue: string) => {
    const payload = {
      unit_title: inputValue,
    };
    const response = await createUnit(payload);

    if (response.success == true) {
      const newOption = { value: response.unit_id, label: inputValue };
      setUnitOptions(prevOptions => [...prevOptions, newOption]);
      setSelectedOption(newOption);
    }
  };

  const handleVatChange = (newValue: OptionType | null) => {
    setSelectedVatOption(newValue);
  };

  const handleVatCreate = async (inputValue: string) => {
    if (/^\d+(\.\d{1,2})?$/.test(inputValue)) {
      const payload = {
        vat_title: inputValue,
      };
      const response = await createVat(payload);

      if (response.success == true) {
        const newOption = { value: response.vat_id, label: inputValue };

        setVatOptions(prevOptions => [...prevOptions, newOption]);
        setSelectedVatOption(newOption);
      }
    }
  };

  const handleSelectProduct = (id: string | number, newValue: InvoiceItem) => {
    setItemDataList(itemDataList =>
      itemDataList.map(item =>
        item.id === id
          ? {
              ...item,
              qty: newValue?.qty,
              description: newValue?.description,
              service_product_number: newValue?.service_product_number,
              invoice_type: newValue?.invoice_type,
              unit_price: newValue?.unit_price,
              unit_type: newValue?.unit_type,
              selected_date: newValue?.selected_date,
              vat_type: newValue?.vat_type,
              total_cost_net:
                Number(newValue?.qty || 0) * Number(newValue?.unit_price || 0),
              total_cost_gross: newValue?.total_cost_gross,
            }
          : item,
      ),
    );
  };

  const handleGoToSubscriptionPage = () => {
    navigate("/subscription");
  };
  return (
    <>
      <div className="right-panel">
        <div className="sticky-top bg-white shadow-sm w-100 p-3 d-flex justify-content-between align-items-center z-index-100">
          <span className="mb-0 default-text" style={{ fontWeight: "500" }}>
            {t("E-Invoices")}
          </span>
          <span className="d-flex gap-3">
            <LanguageSwitcher />
            <Button variant="danger" onClick={handleGoToSubscriptionPage}>
              {t("Unlock Pro")}
            </Button>
          </span>
        </div>

        {/* main section  */}
        <div className="py-3  px-4">
          <div id="breadcrumb" className="d-flex gap-2 my-3">
            <i className="bx bx-home font-size-22"></i>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0 h-5 w-5"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <span>
              <a style={{ fontWeight: "500" }}> {t("E-Invoices")}</a>
            </span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0 h-5 w-5"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <span>
              <a style={{ fontWeight: "500" }}>
                {id ? "Edit Invoice" : "New Invoice"}{" "}
              </a>
            </span>
          </div>
          <Row className="pt-3">
            <Col xl={4} lg={12}>
              <Card className="p-4">
                <CardBody>
                  <Row>
                    <Col lg={4} className="vertical-center">
                      <span className="default-text">{t("Client")}</span>
                    </Col>
                    <Col lg={8}>
                      <ClientCustomDropdown
                        clientData={clientData}
                        handleSelectClient={handleSelectClient}
                        defaultValue={invoiceData?.client?.client_name}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            <Col xl={4} lg={6}>
              <Card className="p-4">
                <CardBody>
                  <Row className="mb-3">
                    <Col lg={4} className="vertical-center">
                      <span>{t("Invoice Date")}</span>
                    </Col>
                    <Col lg={8}>
                      <DatePicker
                        selected={invoiceData?.invoice_date}
                        // onChange={(date: Date | null) => setInvoiceDate(date)}
                        onChange={(date: Date | null) =>
                          setInvoiceData(prevInvoiceData => ({
                            ...prevInvoiceData,
                            invoice_date: date,
                          }))
                        }
                        customInput={<CustomInput />}
                        dateFormat="MM/dd/yyyy" // Customize the date format
                        placeholderText="mm / dd / yyyy"
                        className=" custom-datepicker" // Use Bootstrap styling if needed
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={4} className="vertical-center">
                      <span>{t("Due Date")}</span>
                    </Col>
                    <Col lg={8}>
                      <DatePicker
                        selected={invoiceData?.due_date}
                        onChange={(date: Date | null) =>
                          setInvoiceData(prevInvoiceData => ({
                            ...prevInvoiceData,
                            due_date: date,
                          }))
                        }
                        customInput={<CustomInput />}
                        dateFormat="MM/dd/yyyy" // Customize the date format
                        placeholderText="mm / dd / yyyy"
                        className=" custom-datepicker" // Use Bootstrap styling if needed
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            <Col xl={4} lg={6}>
              <Card className="p-4">
                <CardBody>
                  <Row className="mb-3">
                    <Col lg={4} className="vertical-center">
                      <span className="default-text">{t("Invoice")} #</span>
                    </Col>
                    <Col lg={8}>
                      <FormInput
                        type="text"
                        name="invoice_number"
                        value={invoiceData?.invoice_number}
                        onChange={e =>
                          setInvoiceData(prevInvoiceData => ({
                            ...prevInvoiceData,
                            invoice_number: e.target.value,
                          }))
                        }
                        labelClassName="form-label"
                        className="form-control"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={4} className="vertical-center">
                      <span>PO #</span>
                    </Col>
                    <Col lg={8}>
                      <FormInput
                        type="text"
                        name="po_number"
                        value={invoiceData?.po_number}
                        onChange={(e: any) =>
                          setInvoiceData(prevInvoiceData => ({
                            ...prevInvoiceData,
                            po_number: e.target.value,
                          }))
                        }
                        labelClassName="form-label"
                        className="form-control"
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {itemDataList.map((itemData, index) => (
            <>
              <Card key={itemData.id}>
                <CardBody>
                  <div className=" py-3">
                    <Row className="g-3 mb-3">
                      <Col lg="3">
                        <Form.Group>
                          <Form.Label>{t("Item")}</Form.Label>
                          <ProductCustomDropdown
                            id={itemData.id}
                            allProducts={allProducts}
                            handleSelectProduct={handleSelectProduct}
                            updateItem={updateItem}
                            defaultValue={itemData?.item?.item_id}
                            defaultTitle={itemData?.item?.item_title}
                          />
                        </Form.Group>
                      </Col>

                      <Col lg="3">
                        <FormInput
                          label={t("Product Number")}
                          type="text"
                          name="service_product_number"
                          value={itemData?.service_product_number}
                          onChange={e =>
                            updateItem(
                              itemData.id,
                              "service_product_number",
                              e.target.value,
                            )
                          }
                          labelClassName="form-label"
                          className="form-control"
                        />
                      </Col>

                      <Col lg="3">
                        <Form.Group>
                          <Form.Label>{t("Description")}</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="description"
                            value={itemData?.description}
                            onChange={e =>
                              updateItem(
                                itemData.id,
                                "description",
                                e.target.value,
                              )
                            }
                            rows={4}
                          />
                        </Form.Group>
                      </Col>
                      <Col lg="3">
                        <FormInput
                          label="Qty"
                          type="number"
                          name="qty"
                          value={itemData?.qty}
                          onChange={e =>
                            updateItem(itemData.id, "qty", e.target.value)
                          }
                          min="0"
                          labelClassName="form-label"
                          className="form-control"
                        />
                      </Col>
                    </Row>
                    <Row className="g-3 mb-3">
                      <Col lg="3">
                        <Form.Group>
                          <Form.Label>Service / Product</Form.Label>
                          <Form.Select
                            value={itemData?.invoice_type}
                            onChange={e => {
                              handleInvoiceTypeChange(e.target.value);
                              updateItem(
                                itemData.id,
                                "invoice_type",
                                e.target.value,
                              );
                            }}
                            name="invoice_type"
                          >
                            <option value="1">Service</option>
                            <option value="2">Product</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col lg="3">
                        <Form.Group className="d-flex flex-column">
                          <Form.Label>Unit</Form.Label>
                          <CreatableSelect
                            isClearable
                            options={unitOptions}
                            value={itemData.unit_type}
                            name="unit"
                            onChange={(newValue: any) => {
                              handleChange(newValue);
                              updateItem(
                                itemData.id,
                                "unit_type",
                                newValue ? newValue : null,
                              );
                            }}
                            onCreateOption={handleCreateUnit}
                          />
                        </Form.Group>
                      </Col>
                      <Col lg="3">
                        <FormInput
                          label="Unit Price"
                          type="number"
                          min="0"
                          name="unit_price"
                          value={itemData.unit_price}
                          onChange={e => {
                            updateItem(
                              itemData.id,
                              "unit_price",
                              e.target.value,
                            );
                          }}
                          labelClassName="form-label"
                          className="form-control"
                        />
                      </Col>
                      <Col lg="3">
                        <div className="d-flex flex-column">
                          {selectedInvoiceTypeValue == "1" ? (
                            <>
                              <Form.Label>Service Date</Form.Label>
                              <DatePicker
                                selected={itemData.selected_date}
                                onChange={(date: Date | null) => {
                                  setSelectedDate(date);
                                  updateItem(
                                    itemData.id,
                                    "selected_date",
                                    date ? date.toISOString() : "",
                                  );
                                }}
                                name="selected_date"
                                customInput={<CustomInput />}
                                dateFormat="yyyy-MM-dd" // Customize the date format
                                className=" custom-datepicker" // Use Bootstrap styling if needed
                              />
                            </>
                          ) : (
                            <>
                              <Form.Label> Product Delivery date </Form.Label>
                              <DatePicker
                                selected={selectedDate}
                                onChange={(date: Date | null) => {
                                  setSelectedDate(date);
                                  updateItem(
                                    itemData.id,
                                    "selected_date",
                                    date ? date.toISOString() : "",
                                  );
                                }}
                                name="selected_date"
                                customInput={<CustomInput />}
                                dateFormat="yyyy-MM-dd" // Customize the date format
                                className=" custom-datepicker" // Use Bootstrap styling if needed
                              />
                            </>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-3 mb-3">
                      <Col lg="3">
                        <Form.Group>
                          <Form.Label>VAT %</Form.Label>
                          <CreatableSelect
                            isClearable
                            options={vatOptions}
                            onChange={(newValue: any) => {
                              handleVatChange(newValue);
                              updateItem(
                                itemData.id,
                                "vat_type",
                                newValue ? newValue : null,
                              );
                            }}
                            onCreateOption={handleVatCreate}
                            value={itemData.vat_type}
                            name="vat_option"
                            placeholder="Select or create an option..."
                            onInputChange={(inputValue, { action }) => {
                              if (action === "input-change") {
                                return inputValue.replace(/[^0-9.]/g, ""); // Allow only numbers and dots
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col lg="3">
                        <FormInput
                          label="Total Price (NET) "
                          type="text"
                          name="total_cost_net"
                          value={
                            Number(itemData?.total_cost_net) > 0
                              ? Number(itemData?.total_cost_net).toFixed(2)
                              : "0"
                          }
                          disabled
                          labelClassName="form-label"
                          className="form-control"
                        />
                      </Col>
                      <Col lg="3">
                        <FormInput
                          label="Total Price (GROSS)"
                          type="text"
                          name="total_price_gross"
                          value={
                            Number(itemData?.total_cost_gross) > 0
                              ? Number(itemData?.total_cost_gross).toFixed(2)
                              : "0"
                          }
                          disabled
                          labelClassName="form-label"
                          className="form-control"
                        />
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end">
                      <Button
                        variant="secondary mt-3"
                        size="sm"
                        onClick={() => deleteItem(itemData.id)}
                      >
                        <span style={{ fontWeight: "500" }}>
                          Delete Product <i className="bi bi-trash"></i>
                        </span>
                      </Button>
                      <Button
                        variant={
                          itemData.is_add_discount ? "link" : "secondary"
                        }
                        size="sm"
                        className="ms-3 mt-3"
                        onClick={() =>
                          updateItem(
                            itemData.id,
                            "is_add_discount",
                            !itemData.is_add_discount,
                          )
                        }
                      >
                        <span className=" me-1" style={{ fontWeight: "500" }}>
                          {itemData.is_add_discount
                            ? "Remove Discount"
                            : "Add Discount +"}
                        </span>
                        {itemData.is_add_discount && (
                          <i className="bi bi-trash"></i>
                        )}
                      </Button>
                    </div>
                  </div>
                  {itemData.is_add_discount && (
                    <div className="border-bottom">
                      <Row className="g-3 mb-3 mt-3">
                        <Col lg="3">
                          <Form.Group>
                            <Form.Label>Discount Type </Form.Label>
                            <Form.Select
                              value={itemData.discount_type}
                              onChange={(e: any) => {
                                updateItem(
                                  itemData.id,
                                  "discount_type",
                                  e.target.value,
                                );
                              }}
                              name="discount_type"
                            >
                              <option value="1">Manual Input</option>
                              <option value="2">Discount Percentage</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        {itemData.discount_type == "1" ? (
                          <Col lg="3">
                            <FormInput
                              label="Discount Price(NET) "
                              type="number"
                              name="discount_price"
                              value={itemData.discount_price}
                              onChange={e => {
                                updateItem(
                                  itemData.id,
                                  "discount_price",
                                  e.target.value,
                                );
                              }}
                              labelClassName="form-label"
                              className="form-control"
                            />
                          </Col>
                        ) : (
                          <Col lg="3">
                            <FormInput
                              label="Percentage (%) "
                              type="number"
                              name="discount_percentage"
                              value={itemData.discount_percentage}
                              onChange={e => {
                                updateItem(
                                  itemData.id,
                                  "discount_percentage",
                                  e.target.value,
                                );
                              }}
                              labelClassName="form-label"
                              className="form-control"
                            />
                          </Col>
                        )}

                        <Col lg="3">
                          <FormInput
                            label="Discounted Total Price (NET) "
                            type="number"
                            value={
                              Number(itemData?.discounted_total_price_net) > 0
                                ? Number(
                                    itemData?.discounted_total_price_net,
                                  ).toFixed(2)
                                : "0"
                            }
                            name="discount_total_price_net"
                            min="0"
                            labelClassName="form-label"
                            className="form-control"
                            disabled
                          />
                        </Col>

                        <Col lg="3">
                          <FormInput
                            label="Discounted Total Price (GROSS) "
                            type="number"
                            value={
                              Number(itemData?.discounted_total_price_gross) > 0
                                ? Number(
                                    itemData?.discounted_total_price_gross,
                                  ).toFixed(2)
                                : "0"
                            }
                            min="0"
                            name="discount_total_price_gross"
                            labelClassName="form-label"
                            className="form-control"
                            disabled
                          />
                        </Col>
                      </Row>
                    </div>
                  )}
                </CardBody>
              </Card>
            </>
          ))}

          <Row className="mt-3">
            <Col className="d-flex justify-content-end">
              <Button
                onClick={addItem}
                size="sm"
                style={{
                  fontWeight: "500",
                  backgroundColor: "rgb(17, 125, 192)",
                  marginRight: "20px",
                }}
              >
                <span className="text-white">+ Product</span>
              </Button>
              <Button
                onClick={handleSaveInvoiceData}
                size="sm"
                style={{
                  fontWeight: "500",
                  backgroundColor: "rgb(17, 125, 192)",
                }}
                disabled={loading}
              >
                <span className="text-white">
                  + Save
                  {loading && (
                    <Spinner animation="border" size="sm" className="ms-2" />
                  )}
                </span>
              </Button>
            </Col>
          </Row>
        </div>
      </div>
      <style>{`
       .react-datepicker-wrapper {
         width: 100%
       } 
       .custom-datepicker {
         width: 100%;
       }
       .custom-datepicker:focus {
          outline: none !important; 
          box-shadow: none !important; 
         
       }
        .modal-body-scrollable {
          max-height: 650px;
          overflow-y: auto;
        }
        @media (max-width: 768px) {
          .modal-body-scrollable {
            max-height: 400px;
          }
        }
      `}</style>
    </>
  );
};

export default NewInvoice;
